import { QueryClient } from '@tanstack/react-query';
import apiClient, { safeJson } from '@/api/client';
import { getItemWithExpiration, setItemWithExpiration } from '@/lib/utils';
import { authAccountKeyOf, currentAuthAccountKey } from '@/lib/authSessionGuard';
import { readSharedAuthValue } from '@/lib/sharedAuthStorage';
import { describeSupportError } from './errors';
import { SessionReportRow } from './types';

export type SupportStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'NOT_AN_ISSUE';
export interface SupportRequest {
    supportRequestId: string;
    yearMonth: string;
    rowKey: string;
    coachId: string;
    membershipIds: string[];
    status: SupportStatus;
    adminNote: string;
    /** 관리자가 접수 때 남긴 말. 없으면 '' — 구서버는 칸 자체가 없어요. */
    memo?: string;
    createdAt: string;
    closedAt: string | null;
}
export interface SupportRequestBody {
    requestId: string;
    yearMonth: string;
    rowKey: string;
    coachId: string;
    membershipIds: string[];
    ghostDays: number;
    includeEmpty: boolean;
    /** 선택 메모. 비었으면 키를 아예 넣지 않아요(구서버 호환). 재시도 판정 키에는 넣지 않아요. */
    memo?: string;
}
export type SupportListResult = { status: 'unavailable' } | {
    status: 'ok';
    data: { yearMonth: string; closedVisibleDays: number; openStatuses: SupportStatus[]; results: SupportRequest[]; truncated: boolean };
};
export type SupportPostResult = {
    status: 'ok';
    data: { supportRequest: SupportRequest; created: boolean; reason: 'CREATED' | 'ALREADY_OPEN' | 'REPLAYED' };
} | { status: 'unavailable' } | { status: 'error'; message: string; refresh: boolean; renewRequest: boolean; retryAfterSeconds?: number };

export const supportListKey = (centerId: string, yearMonth?: string) =>
    yearMonth ? ['sessionReportSupport', centerId, yearMonth] as const : ['sessionReportSupport', centerId] as const;
export const supportOptimisticKey = (centerId: string, yearMonth: string) => ['sessionReportSupportOptimistic', centerId, yearMonth] as const;
export const supportMembershipKey = (ids: string[]) => ids.map((id) => id.toLowerCase()).sort().join(',');
export const isOpenSupport = (request: SupportRequest) => request.status === 'OPEN' || request.status === 'IN_PROGRESS';
export const findRowSupport = (results: SupportRequest[], row: SessionReportRow) => {
    const key = supportMembershipKey(row.memberships.map((m) => m.membershipId));
    if (!key) return undefined;
    return results.filter((request) => supportMembershipKey(request.membershipIds) === key)
        .sort((a, b) => Number(isOpenSupport(b)) - Number(isOpenSupport(a)) || Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
};

const path = (centerId: string) => `v1/centers/${centerId}/settlement/session-report/support-requests`;
const readFailure = async (error: any) => {
    let body = error?.body;
    if (!body && error?.response?.clone) {
        try { body = await error.response.clone().json(); } catch { /* 응답 없는 실패도 같은 요청으로 다시 보내요. */ }
    }
    return { status: error?.status ?? error?.response?.status, body };
};
const options = { throwHttpErrors: false, skipErrorAlertFor: [400, 403, 404, 409, 429, 500, 502, 503, 504], retry: 0 };

/**
 * 접수(POST)의 401 처리 — 공용 갱신 경로의 ky(request)는 timeout 을 잃어서 접수 요청은 여기서 한 번만 갱신·재전송한다.
 * 세션 확인은 공용 관문(lib/authSessionGuard)과 같은 규칙이다.
 *  - 소유자 = 이 요청이 실제로 싣는 토큰(이 탭이 자동으로 붙이는 값)과 그때의 계정.
 *  - 「지금」 = 탭이 함께 쓰는 저장소의 최신 값(lib/sharedAuthStorage) — 다른 탭의 로그아웃·다른 계정 로그인·갱신을 본다.
 *  - 다른 계정·로그아웃·취소면 갱신·저장·재전송 모두 하지 않는다. 같은 계정이 이미 갱신했으면 그 토큰으로 재전송(저장 안 함).
 *  - 같은 계정이고 토큰이 그대로일 때만 갱신 토큰을 저장한다(확인 직후, 사이에 await 없음).
 * 접수 멱등 키(body.requestId)·취소 신호·noAuthRetry 는 그대로다.
 */
const postSupportWithAuthRetry = async (centerId: string, body: SupportRequestBody, signal?: AbortSignal) => {
    // 이 요청이 싣는 토큰(apiClient 가 이 탭 저장소에서 붙인다)과 그 계정 — 요청 직전에 잡는다.
    const sentToken = getItemWithExpiration('authorization');
    const ownerAccount = authAccountKeyOf(getItemWithExpiration('authObject'));
    const isOwnerCurrent = () => !signal?.aborted && !!ownerAccount && currentAuthAccountKey() === ownerAccount;
    const requestOptions = { ...options, noAuthRetry: true, timeout: 120000, signal };
    try {
        return await apiClient.post(path(centerId), { ...requestOptions, json: body } as any);
    } catch (error: any) {
        if (!error?.authRetrySkipped || !sentToken || !isOwnerCurrent()) throw error;
        let nextAccessToken = readSharedAuthValue('authorization');
        if (!nextAccessToken) throw error;
        if (nextAccessToken === sentToken) {
            const refreshToken = readSharedAuthValue('refreshToken');
            if (!refreshToken) throw error;
            let refreshedToken: any;
            try {
                const response = await apiClient.post('v1/auth/refresh-tokens', {
                    ...requestOptions, headers: { authorization: sentToken, refreshToken },
                } as any);
                if (response.status === 200) refreshedToken = await safeJson(response);
            } catch { /* 갱신 실패는 원래 인증 오류로 안내해요. */ }
            // 갱신을 기다리는 사이 다른 계정으로 바뀌었거나 취소됐으면 저장도 재전송도 하지 않는다.
            if (!isOwnerCurrent()) throw error;
            // 같은 계정의 다른 요청·다른 탭이 먼저 갱신했다면 그 토큰을 이어받아요(덮지 않는다).
            nextAccessToken = readSharedAuthValue('authorization');
            if (!nextAccessToken) throw error;
            if (nextAccessToken === sentToken) {
                if (!refreshedToken?.access?.token) throw error;
                nextAccessToken = refreshedToken.access.token;
                setItemWithExpiration('authorization', nextAccessToken, refreshedToken.access.expires);
            }
        }
        return apiClient.post(path(centerId), {
            ...requestOptions, json: body, headers: { authorization: nextAccessToken },
        } as any);
    }
};

export const getSessionReportSupport = async (centerId: string, yearMonth: string): Promise<SupportListResult> => {
    let status: number | undefined;
    let data: any;
    try {
        const response = await apiClient.get(path(centerId), { ...options, searchParams: { yearMonth } } as any);
        status = response.status;
        data = await safeJson(response);
    } catch (error) {
        const failure = await readFailure(error);
        status = failure.status;
        data = failure.body;
    }
    if (status === 404) return { status: 'unavailable' };
    if (status === 200 && Array.isArray(data?.results)) return { status: 'ok', data };
    throw new Error(describeSupportError(status, data?.errorCode));
};

export const postSessionReportSupport = async (centerId: string, body: SupportRequestBody, signal?: AbortSignal): Promise<SupportPostResult> => {
    let status: number | undefined;
    let data: any;
    try {
        const response = await postSupportWithAuthRetry(centerId, body, signal);
        status = response.status;
        data = await safeJson(response);
    } catch (error) {
        const failure = await readFailure(error);
        status = failure.status ?? status;
        data = failure.body;
    }
    // 경로가 없는 기본 404만 구서버로 봐요. 회원·강사 범위 오류로 월 목록을 지우지 않아요.
    if (status === 404 && data?.message === 'Not found' && data.errorCode === undefined
        && (data.code === undefined || data.code === 404)) return { status: 'unavailable' };
    if (status === 200 && data?.supportRequest?.supportRequestId && ['CREATED', 'ALREADY_OPEN', 'REPLAYED'].includes(data.reason)) {
        return { status: 'ok', data };
    }
    const code = data?.errorCode;
    const seconds = data?.details?.retryAfterSeconds;
    const retryAfterSeconds = status === 429 && code === 'SESSION_REPORT_SUPPORT_RATE_LIMITED'
        && typeof seconds === 'number' && seconds > 0 && Number.isFinite(Date.now() + Math.ceil(seconds) * 1000)
        ? Math.ceil(seconds) : undefined;
    return { status: 'error', message: describeSupportError(status, code),
        ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
        refresh: status === 404 || (status === 409 && ['SESSION_REPORT_SUPPORT_ROW_NOT_FOUND', 'SESSION_REPORT_SUPPORT_ROW_CHANGED', 'SESSION_REPORT_SUPPORT_NOT_REPORTABLE'].includes(code)),
        // 계약 §2: HTTP 응답을 받았으면 다음 클릭은 새 요청이에요. 응답 유실 때만 같은 번호로 재전송해요.
        renewRequest: typeof status === 'number' && status > 0 };
};

const newRequestId = () => {
    const bytes = new Uint8Array(16);
    if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const SUPPORT_MEMO_MAX = 300;
/** 앞뒤 공백을 지우고 300자로 자른 메모. 공백만이면 빈 문자열이에요. */
export const normalizeSupportMemo = (value: unknown) => typeof value === 'string' ? value.trim().slice(0, SUPPORT_MEMO_MAX) : '';
/** 메모가 있을 때만 memo 키를 붙여요. */
export const withSupportMemo = <T extends object>(body: T, memo: string): T & { memo?: string } => {
    const value = normalizeSupportMemo(memo);
    return value ? { ...body, memo: value } : body;
};

// memo 는 키에 넣지 않아요. 응답을 못 받은 같은 요청 번호는 처음 저장한 본문(메모 포함)을 그대로 다시 보내요.
export const supportAttemptKey = (centerId: string, body: Omit<SupportRequestBody, 'requestId'>) =>
    ['sessionReportSupportAttempt', centerId, body.yearMonth, body.rowKey, body.coachId,
        supportMembershipKey(body.membershipIds), body.ghostDays, body.includeEmpty] as const;

/** 조회 캐시와 분리해 창을 닫거나 다시 계산해도 응답 유실 요청 번호를 보존해요. 로그아웃 시 함께 지워져요. */
export const createSupportAttempt = (client: QueryClient, centerId: string, body: Omit<SupportRequestBody, 'requestId'>) => {
    const key = supportAttemptKey(centerId, body);
    const request = client.getQueryData<SupportRequestBody>(key) ?? { ...body, requestId: newRequestId() };
    client.getQueryCache().build(client, { queryKey: key, cacheTime: Infinity }).setData(request);
    return { key, body: request };
};

export const rememberSupportResult = async (client: QueryClient, centerId: string, yearMonth: string, request: SupportRequest, isCurrent = () => true) => {
    const key = supportListKey(centerId, yearMonth);
    // 실패 안내를 지우지 않아요. 진행 중인 조회만 취소해 늦은 목록이 새 배지를 덮지 않게 해요.
    await client.cancelQueries({ queryKey: supportListKey(centerId), fetchStatus: 'fetching' });
    if (!isCurrent()) return false;
    const visible = isOpenSupport(request) || (request.yearMonth === yearMonth && !!request.closedAt
        && Date.parse(request.closedAt) >= Date.now() - 30 * 24 * 60 * 60 * 1000);
    const merge = (results: SupportRequest[]) => [
        ...(visible ? [request] : []), ...results.filter((item) => item.supportRequestId !== request.supportRequestId),
    ];
    const previous = client.getQueryData<SupportListResult>(key);
    if (previous?.status === 'ok' && client.getQueryState(key)?.status !== 'error') {
        client.setQueryData<SupportListResult>(key, { ...previous, data: { ...previous.data, results: merge(previous.data.results) } });
    } else {
        // 목록 전체를 안다고 꾸미지 않고, 방금 접수한 행의 배지만 따로 기억해요.
        client.setQueryData<SupportRequest[]>(supportOptimisticKey(centerId, yearMonth), (results = []) => merge(results));
    }
    void client.invalidateQueries({ queryKey: supportListKey(centerId), refetchType: 'none' });
    return true;
};
